import type { GetServerSideProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'

// Components
import HelpSearch from '@/components/pages/HelpCenter/components/Search'
import HelpHead from '@/components/pages/HelpCenter/components/HelpHead'

// Libs
import { searchHelpArticles, HelpSearchItem } from '@/api/searchHelpArticles'
import { countryQueryOf, getHelpCountry } from '@/components/pages/HelpCenter/methods'

// Constants
import { ROUTE_PATH } from '@/constants/routePath'

const SEARCH_LIMIT = 30

type Props = {
  keyword: string
  results: HelpSearchItem[]
  countryCode: string
  countryQuery: string | null
}

// _app 以 <Component {...appProps} /> 渲染，getServerSideProps 的資料在 props.pageProps
const HelpSearchPage = ({ pageProps }: { pageProps: Props }) => {
  const { keyword, results, countryCode, countryQuery } = pageProps

  const { t } = useTranslation()

  return (
    <>
      <HelpHead
        title={keyword ? t('Help_Center_Search_Result', { q: keyword, count: results.length }) : undefined}
        canonicalPath={ROUTE_PATH.HELP_SEARCH}
        noIndex
      />
      <HelpSearch keyword={keyword} results={results} countryCode={countryCode} countryQuery={countryQuery ?? undefined} />
    </>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ locale, query }) => {
  const keyword = (typeof query.q === 'string' ? query.q : '').trim().slice(0, 100)
  const countryCode = getHelpCountry(locale, query.country_code)
  const results = keyword ? (await searchHelpArticles({ q: keyword, countryCode, limit: SEARCH_LIMIT })).list ?? [] : []

  return {
    props: {
      keyword,
      results,
      countryCode,
      countryQuery: countryQueryOf(query.country_code) ?? null,
      ...(await serverSideTranslations(locale as string, ['common'])),
    },
  }
}

export default HelpSearchPage
